import * as ko from "knockout";
import type { Lang, Localized, Section, LocalizedTemplate, RootVm, EmailTemplateVM, WhatsAppTarget } from "../types";

declare const notyf: any;

type EventTypeKey =
    | "sofaconcert"
    | "birthday"
    | "corporate_event"
    | "special_occasion"
    | "livestream"
    | "wedding"
    | "other";

// Includes the indefinite article so usages like "für ${eventType}" work
// across genders: "für einen Geburtstag", "für eine Hochzeit", "für ein Event".
// German is accusative (after "für"), English uses a/an, French un/une.
const EVENT_TYPE_LABEL: Record<Lang, Record<EventTypeKey, string>> = {
    de: {
        sofaconcert: "ein Sofakonzert",
        birthday: "einen Geburtstag",
        corporate_event: "ein Firmenevent",
        special_occasion: "einen besonderen Anlass",
        livestream: "ein Livestream-Konzert",
        wedding: "eine Hochzeit",
        other: "ein Event",
    },
    en: {
        sofaconcert: "a sofaconcert",
        birthday: "a birthday",
        corporate_event: "a corporate event",
        special_occasion: "a special occasion",
        livestream: "a livestream concert",
        wedding: "a wedding",
        other: "an event",
    },
    fr: {
        sofaconcert: "un sofaconcert",
        birthday: "un anniversaire",
        corporate_event: "un événement d'entreprise",
        special_occasion: "une occasion spéciale",
        livestream: "un concert en livestream",
        wedding: "un mariage",
        other: "un événement",
    },
};

export type ArtistReminderParams = {
    rootvm: RootVm;
};

export class ArtistReminderTemplateVM implements EmailTemplateVM {
    type = "ArtistReminderTemplate";
    rootvm: RootVm;

    language = ko.observable<Lang>("de");

    subject = ko.observable<string>("");
    hi = ko.observable<string>("");
    greetings = ko.observable<string>("");
    subject_template = ko.observable<Localized>({ de: "", en: "", fr: "" });
    hi_template = ko.observable<Localized>({ de: "", en: "", fr: "" });
    greetings_template = ko.observable<Localized>({ de: "", en: "", fr: "" });

    sections: ko.ObservableArray<Section>;

    artist_uuid = ko.observable<string>("");
    artist_name = ko.observable<string>("");
    artist_phone = ko.observable<string>("");
    display_name = ko.observable<string>("");
    event_uuid = ko.observable<string>("");
    event_type = ko.observable<string>("other");
    event_date = ko.observable<string | undefined>(undefined);
    event_location = ko.observable<string>("");

    pretty_event_date: ko.PureComputed<string>;
    pretty_event_type: ko.PureComputed<string>;

    constructor(params: ArtistReminderParams) {
        this.rootvm = params.rootvm;

        this.sections = ko.observableArray<Section>([this.makeSection("Body", 12)]);

        this.pretty_event_date = ko.pureComputed(() => {
            const d = this.event_date();
            if (!d) return "";
            return new Date(d).toLocaleDateString(this.language());
        });

        this.pretty_event_type = ko.pureComputed(() => {
            const t = (this.event_type() || "other") as EventTypeKey;
            return EVENT_TYPE_LABEL[this.language()]?.[t] ?? t;
        });

        this.language.subscribe((lang) => {
            if (lang) this.populate(lang);
        });
    }

    private makeSection(label: string, rows: number): Section {
        return {
            label,
            value: ko.observable<string>(""),
            template: ko.observable<Localized>({ de: "", en: "", fr: "" }),
            rows,
        };
    }

    private buildTemplate(): LocalizedTemplate {
        const artist = this.artist_name();
        const display = this.display_name();
        const date = this.pretty_event_date();
        const location = this.event_location();
        const eventType = this.pretty_event_type();

        return {
            subject: {
                de: `Neue Buchungsanfrage für ${eventType} am ${date}`,
                en: `New booking enquiry for ${eventType} on ${date}`,
                fr: `Nouvelle demande de réservation pour ${eventType} le ${date}`,
            },
            hi: {
                de: `*Hey ${artist}*,`,
                en: `*Hey ${artist}*,`,
                fr: `*Salut ${artist}*,`,
            },
            sections: [
                {
                    de: `${display} von SofaConcerts hier 👋\n\nDu hast eine neue Buchungsanfrage – für *${eventType}* am *${date}* in *${location}*.\n\nSchau gerne einmal zeitnah nach und lass den Gastgeber wissen, ob der Gig für dich in Frage kommt.\nDer Host freut sich über eine schnelle Rückmeldung, damit er planen kann – und je schneller du antwortest,\ndesto höher ist die Wahrscheinlichkeit, dass die Buchung klappt 😊\n\nWenn du noch Fragen hast oder etwas unklar ist, kannst du mir natürlich jederzeit schreiben.`,
                    en: `${display} from SofaConcerts here 👋\n\nYou've got a new booking enquiry – for *${eventType}* on *${date}* in *${location}*.\n\nPlease take a quick look soon and let the host know whether the gig could be a fit for you.\nThe host would really appreciate a quick reply so they can plan – and the faster you respond,\nthe higher the chance the booking actually happens 😊\n\nIf you have any questions or anything is unclear, feel free to message me anytime.`,
                    fr: `${display} de SofaConcerts ici 👋\n\nVous avez une nouvelle demande de réservation – pour *${eventType}* le *${date}* à *${location}*.\n\nJette un œil rapidement et fais savoir à l'organisateur si le concert pourrait te convenir.\nL'organisateur apprécierait une réponse rapide pour pouvoir planifier – et plus vous répondez vite,\nplus les chances que la réservation aboutisse sont élevées 😊\n\nSi vous avez des questions ou que quelque chose n'est pas clair, n'hésitez pas à m'écrire.`,
                },
            ],
            greetings: {
                de: `Danke dir & viele Grüße\n*${display}*`,
                en: `Thanks & best regards\n*${display}*`,
                fr: `Merci & cordialement\n*${display}*`,
            },
        };
    }

    populate = (language: Lang = this.language()) => {
        const tpl = this.buildTemplate();

        this.subject_template(tpl.subject);
        this.hi_template(tpl.hi);
        this.greetings_template(tpl.greetings);

        tpl.sections.forEach((localized, i) => {
            const section = this.sections()[i];
            if (!section) return;
            section.template(localized);
            section.value(localized[language] ?? "");
        });

        this.subject(tpl.subject[language] ?? "");
        this.hi(tpl.hi[language] ?? "");
        this.greetings(tpl.greetings[language] ?? "");
    };

    whatsappTarget = (): WhatsAppTarget | null => {
        const phone = this.artist_phone();
        if (!phone) return null;
        const text = [
            this.hi(),
            ...this.sections().map((s) => s.value()),
            this.greetings(),
        ].filter(Boolean).join("\n\n");
        return { phone, text };
    };

    send_mail = () => {
        const data = {
            subject: this.subject(),
            hi: this.hi(),
            sections: this.sections().map((s) => s.value()),
            greetings: this.greetings(),
            artist_uuid: this.artist_uuid(),
            event_uuid: this.event_uuid(),
            locale: this.language(),
        };
        return fetch("/api/admin/artist/send-new-invite-notification", {
            method: "POST",
            body: JSON.stringify(data),
        })
            .then((r) => r.json())
            .then(() => notyf.success("Reminder sent."));
    };
}
